import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/* ═══════════════════════════════════════════════════════════════
   BLOG CARDS — Homepage section showing published blogs from API
   Layout: Image left, Title + Excerpt + See more on right
   Read-only, no edit options
   ═══════════════════════════════════════════════════════════════ */

const PLACEHOLDER_IMG = "https://cdn.shopify.com/s/files/1/0644/4086/0781/files/Shopify-webs-1.png?v=1765272625";

export default function BlogCards() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const apiBase = window.location.hostname === "localhost"
      ? "http://localhost:8081"
      : "/api";
    fetch(`${apiBase}/blog-api.php?action=list&status=published`)
      .then(r => r.json())
      .then(d => {
        if (d.posts) setPosts(d.posts.slice(0, 6));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  // Don't render section if no posts
  if (!loading && posts.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      style={{
        padding: "80px 20px",
        background: "#fff",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: "all 0.8s ease",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h2
            className="global-body-heading-fonts"
            style={{
              fontSize: "clamp(28px,4vw,38px)",
              fontWeight: 700,
              color: "#1a1a1a",
              marginBottom: 12,
            }}
          >
            Latest From Our Blog
          </h2>
          <p style={{ fontSize: 16, color: "#666", maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>
            Insights, strategies, and tips to grow your digital business.
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
            Loading articles...
          </div>
        ) : (
          <>
            {/* Blog Grid */}
            <div style={{ display: "grid", gap: 20 }}>
              {posts.map((post, i) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.id || i}
                  className="blog-card-responsive"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "grid",
                    gridTemplateColumns: post.image ? "240px 1fr" : "1fr",
                    gap: 0,
                    background: "#f9fafb",
                    borderRadius: 16,
                    overflow: "hidden",
                    border: "1px solid #f0f0f0",
                    transition: "all .3s ease",
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? "translateY(0)" : "translateY(20px)",
                    transitionDelay: `${i * 0.08}s`,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 12px 36px rgba(0,0,0,.08)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {/* Image */}
                  <div style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 160,
                    background: `url(${post.image || PLACEHOLDER_IMG}) center/cover no-repeat`,
                  }} />

                  {/* Content */}
                  <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "3px 10px",
                        borderRadius: 20, background: "#dbeafe", color: "#2563eb",
                      }}>
                        {post.category || "Article"}
                      </span>
                      <span style={{ fontSize: 11, color: "#aaa" }}>
                        {post.created_at ? new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </span>
                      {post.author && (
                        <span style={{ fontSize: 11, color: "#aaa" }}>
                          By {post.author}
                        </span>
                      )}
                    </div>

                    <h3 style={{
                      fontSize: 18, fontWeight: 700, color: "#1a1a1a",
                      lineHeight: 1.35, marginBottom: 8,
                    }}>
                      {post.title}
                    </h3>

                    {post.excerpt && (
                      <p style={{
                        fontSize: 14, color: "#666", lineHeight: 1.6,
                        marginBottom: 12,
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {post.excerpt}
                      </p>
                    )}

                    <span style={{
                      fontSize: 13, fontWeight: 600, color: "#2563eb",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}>
                      See more →
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* View All Button */}
            <div style={{ textAlign: "center", marginTop: 36 }}>
              <Link
                to="/blogs"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "14px 32px", borderRadius: 10,
                  background: "#2563eb", color: "#fff",
                  fontSize: 15, fontWeight: 600,
                  textDecoration: "none",
                  transition: "all .3s",
                  boxShadow: "0 4px 16px rgba(37,99,235,.3)",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "#000";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "#2563eb";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                View All Articles
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 640px) {
          .blog-card-responsive { grid-template-columns: 1fr !important; }
          .blog-card-responsive > div:first-child { min-height: 200px !important; }
        }
      `}</style>
    </section>
  );
}
